import * as React from 'react';
import { useIntl } from 'react-intl';
import { createColumnHelper, getSortedRowModel } from '@tanstack/react-table';

import { DataTable, DataTableColumnHeader } from 'src/registry/components/table';
import { CURRENCY } from 'src/types';
import { commonMessages } from 'src/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { financialDocumentRowMessages } from 'src/features/financialDocumentRows/utils/messages';
import { unitsMessages } from 'src/features/units/utils/messages';
import { taxReasonsMessages } from 'src/features/taxReasons/utils/messages';
import { SimulationFinancialDocumentRow } from 'src/features/billingSimulation/utils/types';
import { Amount } from 'src/registry/components/Amount';

type BillingSimulationFinancialDocumentRowsTableProps = {
    rows: SimulationFinancialDocumentRow[];
    currency: CURRENCY;
};

const BillingSimulationFinancialDocumentRowsTable = ({
    rows,
    currency,
}: BillingSimulationFinancialDocumentRowsTableProps) => {
    const { formatMessage } = useIntl();

    const columnHelper = React.useMemo(() => createColumnHelper<SimulationFinancialDocumentRow>(), []);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('number', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.number,
                meta: { title: formatMessage(financialDocumentRowMessages.number) },
                maxSize: 45,
            }),
            columnHelper.accessor('name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.name,
                meta: { title: formatMessage(commonMessages.name) },
            }),
            columnHelper.accessor('unit_name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.unit_name || '-',
                meta: { title: formatMessage(unitsMessages.unit) },
            }),
            columnHelper.accessor('tax_reason_name', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => {
                    const value = (Number(row.original.tax_reason_value) * 100).toFixed(2);
                    return `${row.original.tax_reason_name} - ${value}%`;
                },
                meta: { title: formatMessage(taxReasonsMessages.taxReason) },
                minSize: 160,
            }),
            columnHelper.accessor('price', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => <Amount value={row.original.price} currency={currency} />,
                meta: { title: formatMessage(financialDocumentRowMessages.unitPrice) },
            }),
            columnHelper.accessor('quantity', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => row.original.quantity,
                meta: { title: formatMessage(financialDocumentRowMessages.quantity) },
            }),
            columnHelper.accessor('amount', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => <Amount value={row.original.amount} currency={currency} />,
                meta: { title: formatMessage(financialDocumentRowMessages.lineTotal) },
            }),
            columnHelper.accessor('tax_percent', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => `${(Number(row.original.tax_percent) * 100).toFixed(2)}%`,
                meta: { title: formatMessage(financialDocumentRowMessages.taxPercent) },
            }),
            columnHelper.accessor('amount_with_tax', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => <Amount value={row.original.amount_with_tax} currency={currency} />,
                meta: { title: formatMessage(paymentsMessages.amountWithTax) },
            }),
        ],
        [columnHelper, currency, formatMessage],
    );

    const data = React.useMemo(() => ({ count: rows.length, next: null, previous: null, results: rows }), [rows]);

    return (
        <DataTable
            columns={columns}
            data={data}
            enableSearchParams={false}
            getSortedRowModel={getSortedRowModel()}
            getRowId={(row) => row.id}
            semantics={{
                title: formatMessage(financialDocumentRowMessages.rowsTitle),
                entryName: formatMessage(financialDocumentRowMessages.row).toLowerCase(),
                entryNames: formatMessage(financialDocumentRowMessages.rows).toLowerCase(),
            }}
        />
    );
};

export default BillingSimulationFinancialDocumentRowsTable;
